import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Percent, Search } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchFeatureFlags, setFeatureFlag, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'
import { cn } from '@/lib/cn'

/** Stored flag value → current rollout percent (0–100). "N%" → N; true/1/on → 100; else 0. */
function valueToPct(v: string): number {
  const s = (v ?? '').trim()
  if (s.endsWith('%')) {
    const n = parseInt(s.slice(0, -1), 10)
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0
  }
  return ['1', 'true', 'on', 'yes'].includes(s.toLowerCase()) ? 100 : 0
}

/** Percent → stored value. Extremes normalise to true/false so the flag list's
 *  `enabled` column stays correct; in-between is a gradual "N%" rollout. */
function pctToValue(pct: number): string {
  if (pct <= 0) return 'false'
  if (pct >= 100) return 'true'
  return `${pct}%`
}

const PRESETS = [0, 10, 25, 50, 75, 100]

/**
 * A14.04 — Bosqichma-bosqich yoyish (rollout %). Har flag foydalanuvchilarning
 * `N%` iga yoqiladi; backend `GET /settings/features`ни (flag,userID) bo'yicha
 * BARQAROR bucketlab har userga bir xil «true/false» qaytaradi (foiz oshsa —
 * yoqilganlar to'plami faqat KENGAYADI). Yozuv `POST /admin/settings/flags`
 * (value:"N%") + audit. AUDIT_VIEW-gated.
 */
export function FeatureRollout() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState<Record<string, number>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => {
        setFlags(r.rows)
        setDraft(Object.fromEntries(r.rows.map((f) => [f.key, valueToPct(f.value)])))
      })
      .catch(() => setFlags([]))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const list = flags ?? []
    return s ? list.filter((f) => f.key.toLowerCase().includes(s)) : list
  }, [flags, q])

  const save = (f: AdminFeatureFlag) => {
    if (busy) return
    const pct = draft[f.key] ?? valueToPct(f.value)
    const value = pctToValue(pct)
    setBusy(f.key)
    setFeatureFlag(f.key, { value })
      .then(() => {
        setFlags((fs) => fs?.map((x) => (x.key === f.key ? { ...x, value, enabled: pct >= 100 } : x)) ?? null)
        toast(t.flags.rolloutSaved(pct), { tone: 'success' })
      })
      .catch((e: unknown) => toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(null))
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.rolloutTitle}
        screenId="A14.04"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <p className="border-b border-border-subtle px-3 py-2 text-xs text-tertiary">{t.flags.rolloutNote}</p>
      <div className="border-b border-border-subtle p-3">
        <div className="flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
          <Search size={14} className="text-tertiary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.loc.searchPh}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {flags === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-tertiary">
            <Percent size={22} className="mx-auto mb-2 opacity-60" />
            {t.states.emptyTitle}
          </div>
        ) : (
          <div className="max-w-2xl space-y-3">
            {filtered.map((f) => {
              const pct = draft[f.key] ?? valueToPct(f.value)
              const dirty = pct !== valueToPct(f.value)
              return (
                <div key={f.key} className="rounded-lg border border-border-default bg-surface-1 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 truncate font-mono text-sm text-primary" title={f.key}>{f.key}</span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-primary">{pct}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={pct}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: Number(e.target.value) }))}
                    className="mt-2 w-full accent-accent"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {PRESETS.map((p) => (
                      <button
                        key={p}
                        onClick={() => setDraft((d) => ({ ...d, [f.key]: p }))}
                        className={cn(
                          'rounded border px-2 py-0.5 text-xs',
                          pct === p ? 'border-accent bg-accent text-white' : 'border-border-default bg-surface-2 text-secondary hover:text-primary',
                        )}
                      >
                        {p}%
                      </button>
                    ))}
                    <button
                      onClick={() => save(f)}
                      disabled={!dirty || busy === f.key}
                      className="ml-auto rounded-md bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-40"
                    >
                      {t.common.save}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
